import { Method } from 'axios';
import { CreatePersonInput } from './create-person';
import { Executable } from './executable';

/**
 * The input object to the IssueEventWallet class constructor.
 */
export interface IssueEventWalletInput {
  /**
   * Person record used to create a wallet card
   */
  person: CreatePersonInput;

  /**
   * Wallet Template Identifier.  If not provided, will use the tenant default template.
   */
  walletTemplateId?: number | string;

  /**
   * Wallet Template Tier Identifier.  If not provided, will use the tenant default tier.
   */
  walletTemplateTierId?: number | string;

  /**
   * Brand Identifier.  If not provided, will use the tenant default brand.
   */
  brandId?: number;

  /**
   * Program Identifier.  If not provided, will use the brand default program.
   */

  programId?: number;
}

/**
 * The response expected from the IssueEventWallet execution within BambuClient.
 */
export interface IssueEventWalletResponse {
  /**
   * Unique identifier for the wallet pass.
   */
  id: string;
  /**
   * The person identifier.
   */
  externalId: string;
  /**
   * The type of card identifier.
   */
  type: string;
  /**
   * The type of card identifier.
   */
  tierId: string;
  /**
   * The type of card identifier.
   */
  profileImage: string;
  /**
   * The person.
   */
  person: any;
  /**
   * The metadata.
   */
  metaData: any;
  /**
   * The optOut.
   */
  optOut: boolean;
  /**
   * The number of points.
   */
  points: number;
  /**
   * The expiration date.
   */
  expiryDate: Date;
  /**
   * The status of the card.
   */
  status: string;
}

/**
 * Creates a product for the provided tenant to be able to generate and mint NFTs from.
 */
export class IssueEventWallet extends Executable<IssueEventWalletResponse> {
  /**
   * getMethod is used by the BambuClient to properly construct the request call.
   * @returns request method
   */
  public getMethod(): Method {
    return 'post';
  }

  /**
   * getResponse is used by the BambuClient to propery return the response object type.
   * @param apiResponse the api response object returned by the BambuMeta client before being converted to the IssueEventWalletResponse
   * @returns IssueEventWalletResponse
   */
  public getResponse(apiResponse: any): IssueEventWalletResponse {
    return apiResponse;
  }

  /**
   * getURLPath is used by the BambuClient to properly construct the request call.
   * @param tenantId the tenant id defined in the BambuClient
   * @returns request url
   */
  public getURLPath(tenantId?: string): string {
    if (this.brandId && this.programId) {
      return `${this.brandId}/programs/${this.programId}/issue-wallet`;
    }
    if (this.brandId) {
      return `${this.brandId}/issue-wallet`;
    }
    return `${tenantId}/issue-event-wallet`;
  }

  public person?: CreatePersonInput;

  public walletTemplateId?: number | string;

  public walletTemplateTierId?: number | string;

  public brandId?: number;

  public programId?: number;

  constructor(input?: IssueEventWalletInput) {
    super();
    if (input) {
      Object.assign(this, input);
    }
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): any {
    const input: any = {
      person: this.person,
      walletTemplateId: this.walletTemplateId,
      walletTemplateTierId: this.walletTemplateTierId,
    };
    return input;
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    if (this.brandId) {
      return 'brands';
    }
    return undefined;
  }
}
